/**
 * Reconciling the two places a client's SOAs live.
 *
 * `savedCaseStudies` is the in-app WORKING COPY, held in sessionStorage: it carries the full snapshot
 * needed to reopen and re-render a document. The `soas` table is the ADVICE REGISTER — immutable,
 * versioned, licensee-attributed, the legal record.
 *
 * The Partner Portal's Reports & SOAs list was built from the working copy alone, so signing out
 * emptied it: a client's generated advice vanished from the portal even though every version was
 * safely stored server-side, and a draft sitting unsent could be neither seen nor published.
 *
 * Working copy wins where both exist (only it can reopen the document). Register-only versions are
 * appended and flagged, so nothing is invisible and the UI can offer only what actually works for
 * them — publishing, yes; reopening or deleting, no (the register has no DELETE grant at all).
 */
import type { SoaScenario } from './PartnerPortal.tsx';
import type { RegisteredSoaSummary } from '../../services/adviceApi.ts';

const MODULE_LABELS: Record<string, string> = {
    aged_care: 'Aged Care',
    support_at_home: 'Support at Home',
    retirement_village: 'Retirement Village',
    downsizer: 'Downsizer',
    granny_flat: 'Granny Flat',
    cashflow_pension: 'Cashflow & Pension',
};

/** How a register-only version is labelled in the picker, e.g. "v2 · Aged Care". */
export function registeredSoaLabel(soa: RegisteredSoaSummary): string {
    return `v${soa.version} · ${MODULE_LABELS[soa.module] || soa.module}`;
}

export function mergeSoaSources(
    savedCaseStudies: SoaScenario[] | null | undefined,
    registeredSoasByProfileId: Record<string, RegisteredSoaSummary[]> | null | undefined,
): Map<string, SoaScenario[]> {
    const grouped = new Map<string, SoaScenario[]>();
    (savedCaseStudies || []).forEach((scenario) => {
        if (!scenario?.caseId) return;
        if (!grouped.has(scenario.caseId)) grouped.set(scenario.caseId, []);
        grouped.get(scenario.caseId)!.push(scenario);
    });

    Object.entries(registeredSoasByProfileId || {}).forEach(([profileId, registered]) => {
        const existing = grouped.get(profileId) || [];
        // Match on the register id the working copy recorded when it was written there.
        const alreadyLocal = new Set(existing.map((sc) => String(sc.serverSoaId || '')).filter(Boolean));
        const extras: SoaScenario[] = (registered || [])
            .filter((soa) => !alreadyLocal.has(soa.id))
            .map((soa) => ({
                id: soa.id,
                caseId: profileId,
                name: registeredSoaLabel(soa),
                createdAt: soa.createdAt,
                soaDeliveryStatus: soa.status,
                serverSoaId: soa.id,
                registerOnly: true,
            }));
        if (extras.length) grouped.set(profileId, [...existing, ...extras]);
    });

    grouped.forEach((items) => {
        items.sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime());
    });
    return grouped;
}
